/**
 * Return Photo Gallery Component
 *
 * Displays return photos in a grid with lightbox functionality.
 * Supports both booking-level and item-level photos.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Download,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import type { BookingReturnPhoto } from '../types/return-reports';

interface ReturnPhotoGalleryProps {
  photos: BookingReturnPhoto[];
  title?: string;
  itemContext?: string; // e.g., "Mountain Bike #MTB-001"
}

export default function ReturnPhotoGallery({
  photos,
  title = 'Photos',
  itemContext,
}: ReturnPhotoGalleryProps) {
  const { t } = useTranslation();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoom, setZoom] = useState(1);

  const openLightbox = (index: number) => {
    setCurrentIndex(index);
    setZoom(1);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setZoom(1);
  };

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
    setZoom(1);
  }, [photos.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
    setZoom(1);
  }, [photos.length]);

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.5, 0.5));
  };

  const handleDownload = () => {
    const photo = photos[currentIndex];
    if (photo?.public_url) {
      const link = document.createElement('a');
      link.href = photo.public_url;
      link.download = photo.file_name || 'return-photo.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          closeLightbox();
          break;
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'ArrowRight':
          goToNext();
          break;
        case '+':
        case '=':
          handleZoomIn();
          break;
        case '-':
          handleZoomOut();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, goToPrevious, goToNext]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (lightboxOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [lightboxOpen]);

  if (photos.length === 0) {
    return (
      <div className="text-center py-md text-secondary text-sm">
        {t('returnPhotoGallery.noPhotos')}
      </div>
    );
  }

  const currentPhoto = photos[currentIndex];

  return (
    <>
      {/* Gallery Header */}
      {title && (
        <div className="flex items-center justify-between mb-sm">
          <h4 className="text-sm font-medium text-primary">{title}</h4>
          <span className="text-xs text-secondary">{t('returnPhotoGallery.photoCount', { count: photos.length })}</span>
        </div>
      )}

      {/* Thumbnail Grid */}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-sm">
        {photos.map((photo, index) => (
          <button
            key={photo.id}
            onClick={() => openLightbox(index)}
            className="aspect-square rounded-lg overflow-hidden bg-secondary hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-blue"
          >
            <img
              src={photo.public_url}
              alt={`${t('returnPhotoGallery.returnPhoto')} ${index + 1}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </button>
        ))}
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Image Container with Controls */}
          <div
            className="relative"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '90vw',
              maxHeight: 'calc(100vh - 80px)',
            }}
          >
            {/* Top toolbar - positioned at top edge of image container */}
            <div className="absolute -top-10 left-0 right-0 flex items-center justify-between z-10">
              <div className="flex items-center gap-2">
                <button
                  onClick={handleZoomOut}
                  disabled={zoom <= 0.5}
                  className="p-2 text-white/80 hover:text-white transition-colors disabled:opacity-30"
                  aria-label={t('returnPhotoGallery.zoomOut')}
                >
                  <ZoomOut className="w-5 h-5" />
                </button>
                <span className="text-white/80 text-sm min-w-[3rem] text-center">
                  {Math.round(zoom * 100)}%
                </span>
                <button
                  onClick={handleZoomIn}
                  disabled={zoom >= 3}
                  className="p-2 text-white/80 hover:text-white transition-colors disabled:opacity-30"
                  aria-label={t('returnPhotoGallery.zoomIn')}
                >
                  <ZoomIn className="w-5 h-5" />
                </button>
                <button
                  onClick={handleDownload}
                  className="p-2 text-white/80 hover:text-white transition-colors ml-2"
                  aria-label={t('returnPhotoGallery.download')}
                >
                  <Download className="w-5 h-5" />
                </button>
              </div>
              <button
                onClick={closeLightbox}
                className="p-2 text-white/80 hover:text-white transition-colors"
                aria-label={t('returnPhotoGallery.close')}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Navigation - Previous (at left edge of image) */}
            {photos.length > 1 && (
              <button
                onClick={goToPrevious}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full p-2 text-white/80 hover:text-white transition-colors"
                aria-label={t('returnPhotoGallery.previousPhoto')}
              >
                <ChevronLeft className="w-10 h-10" />
              </button>
            )}

            {/* Main Image */}
            <div
              className="overflow-auto"
              style={{
                maxWidth: '90vw',
                maxHeight: 'calc(100vh - 120px)',
              }}
            >
              <img
                src={currentPhoto?.public_url}
                alt={`${t('returnPhotoGallery.returnPhoto')} ${currentIndex + 1}`}
                className="transition-transform duration-200"
                style={{
                  maxWidth: zoom === 1 ? '90vw' : 'none',
                  maxHeight: zoom === 1 ? 'calc(100vh - 120px)' : 'none',
                  width: zoom === 1 ? 'auto' : undefined,
                  height: zoom === 1 ? 'auto' : undefined,
                  transform: zoom !== 1 ? `scale(${zoom})` : undefined,
                  transformOrigin: 'center center',
                  objectFit: 'contain',
                }}
              />
            </div>

            {/* Navigation - Next (at right edge of image) */}
            {photos.length > 1 && (
              <button
                onClick={goToNext}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full p-2 text-white/80 hover:text-white transition-colors"
                aria-label={t('returnPhotoGallery.nextPhoto')}
              >
                <ChevronRight className="w-10 h-10" />
              </button>
            )}

            {/* Footer Info - positioned at bottom edge of image container */}
            <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-center">
              <p className="text-white/80 text-sm">
                {currentIndex + 1} / {photos.length}
              </p>
              {itemContext && (
                <p className="text-white/60 text-xs mt-1">{itemContext}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
